import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  X,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  FolderOpen,
  ZoomIn,
  ExternalLink,
  Trash2,
  Copy,
  FolderInput,
  Check,
  CheckSquare,
  Square,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ImageGallery({ orderId, open, onClose, onImagesUpdated }) {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [orderInfo, setOrderInfo] = useState({});
  const [folderPath, setFolderPath] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [moving, setMoving] = useState(false);

  const fetchAvailableSessions = async () => {
    try {
      const resp = await axios.get(`${API}/orders/${orderId}/images`);
      const phone = resp.data.phone_number;
      const ordersResp = await axios.get(`${API}/orders/by-phone/${phone}`);
      const ordersList = Array.isArray(ordersResp.data) ? ordersResp.data : (ordersResp.data.orders || []);
      const sessions = ordersList
        .filter(o => o.id !== orderId && o.session_id)
        .map(o => ({ session_id: o.session_id, order_id: o.id, image_count: o.total_image_count }));
      setAvailableSessions(sessions);
    } catch (e) {
      toast.error("Failed to fetch sessions");
    }
  };

  const handleMoveImages = async (targetSession) => {
    if (selectedImages.length === 0) return;
    setMoving(true);
    try {
      const resp = await axios.post(`${API}/orders/${orderId}/move-images`, {
        image_filenames: selectedImages,
        target_session: targetSession
      });
      toast.success(`Moved ${resp.data.moved} image(s) to ${targetSession}`);
      setSelectedImages([]);
      setShowMoveDialog(false);
      fetchImages();
      onImagesUpdated?.();
    } catch (e) {
      toast.error("Failed to move images");
    } finally {
      setMoving(false);
    }
  };
  const [editingCaption, setEditingCaption] = useState(null);
  const [captionText, setCaptionText] = useState("");
  const [savingCaption, setSavingCaption] = useState(false);

  const handleSaveCaption = async (imagePath) => {
    try {
      setSavingCaption(true);
      await axios.put(`${API}/orders/${orderId}/images/caption`,
        { caption: captionText },
        { params: { image_path: imagePath } }
      );
      toast.success("Caption saved!");
      setEditingCaption(null);
      setCaptionText("");
      fetchImages();
      onImagesUpdated?.();
    } catch (error) {
      toast.error("Failed to save caption");
    } finally {
      setSavingCaption(false);
    }
  };

  const handleDuplicate = async () => {
    if (selectedImages.length === 0) return;
    setDuplicating(true);
    try {
      // selectedImages contains filenames like IMG_001.jpg
      // folderPath contains date/phone.session like 2026-07-17/919XXXXXX.S1
      const imagePaths = selectedImages.map(filename => 
        `/var/www/thunderbuddy/backend/storage/images/${folderPath}/${filename}`
      );
      const response = await axios.post(`${API}/orders/${orderId}/duplicate-images`, {
        image_paths: imagePaths
      });
      toast.success(`Duplicated ${response.data.duplicated} image(s)`);
      setSelectedImages([]);
      fetchImages();
      onImagesUpdated?.();
    } catch (error) {
      toast.error("Failed to duplicate images");
    } finally {
      setDuplicating(false);
    }
  };

  useEffect(() => {
    if (open && orderId) {
      fetchImages();
      setSelectedImages([]); // Reset selection when popup opens
    }
  }, [open, orderId]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/orders/${orderId}/images`);
      setImages(response.data.images || []);
      setOrderInfo({
        phoneNumber: response.data.phone_number,
        sessionId: response.data.session_id,
        totalImages: response.data.total_images,
        imageFolder: response.data.image_folder,
      });
      
      // Extract folder path from response or first image
      let extractedPath = null;
      
      // First try from image_folder in response
      if (response.data.image_folder) {
        const fullPathMatch = response.data.image_folder.match(/(\d{4}-\d{2}-\d{2}\/[^/]+)$/);
        if (fullPathMatch) {
          extractedPath = fullPathMatch[1];
        }
      }
      
      // Fallback: extract from first image path
      if (!extractedPath && response.data.images && response.data.images.length > 0) {
        const firstImagePath = response.data.images[0].path;
        const match = firstImagePath.match(/\/api\/images\/([^/]+\/[^/]+)\//);
        if (match) {
          extractedPath = match[1];
        }
      }
      
      // Fallback: construct from phone and session
      if (!extractedPath && response.data.phone_number) {
        const today = new Date().toISOString().split('T')[0];
        extractedPath = `${today}/${response.data.phone_number}${response.data.session_id ? '.' + response.data.session_id : ''}`;
      }
      
      setFolderPath(extractedPath);
    } catch (error) {
      console.error("Error fetching images:", error);
      toast.error("Failed to load images");
    } finally {
      setLoading(false);
    }
  };

  const openInImageGallery = () => {
    const phone = orderInfo?.phoneNumber;
    if (phone) {
      navigate(`/images?search=${encodeURIComponent(phone)}`);
      onClose();
    } else if (folderPath) {
      navigate(`/images?folder=${encodeURIComponent(folderPath)}`);
      onClose();
    } else {
      toast.error("Phone number not available");
    }
  };

  // Delete single image
  const handleDeleteImage = async (filename) => {
    try {
      await axios.delete(`${API}/orders/${orderId}/images/${filename}`);
      toast.success("Image moved to trash");
      fetchImages();
      onImagesUpdated?.();
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    }
  };

  // Selection handlers
  const toggleImageSelection = (filename, e) => {
    e?.stopPropagation();
    setSelectedImages(prev => 
      prev.includes(filename)
        ? prev.filter(f => f !== filename)
        : [...prev, filename]
    );
  };

  const toggleSelectAll = () => {
    if (selectedImages.length === images.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(images.map(img => img.filename));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedImages.length === 0) return;
    
    try {
      setBulkDeleting(true);
      let successCount = 0;
      let errorCount = 0;
      
      for (const filename of selectedImages) {
        try {
          await axios.delete(`${API}/orders/${orderId}/images/${filename}`);
          successCount++;
        } catch (error) {
          console.error(`Error deleting ${filename}:`, error);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} image(s) moved to trash`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to delete ${errorCount} image(s)`);
      }
      
      setSelectedImages([]);
      fetchImages();
      onImagesUpdated?.();
    } catch (error) {
      console.error("Error in bulk delete:", error);
      toast.error("Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const isAllSelected = images.length > 0 && selectedImages.length === images.length;

  const handlePrevImage = () => {
    const currentIndex = images.findIndex((img) => img.path === selectedImage);
    if (currentIndex > 0) {
      setSelectedImage(images[currentIndex - 1].path);
    }
  };

  const handleNextImage = () => {
    const currentIndex = images.findIndex((img) => img.path === selectedImage);
    if (currentIndex < images.length - 1) {
      setSelectedImage(images[currentIndex + 1].path);
    }
  };

  const handleKeyDown = (e) => {
    if (selectedImage) {
      if (e.key === "ArrowLeft") handlePrevImage();
      if (e.key === "ArrowRight") handleNextImage();
      if (e.key === "Escape") setSelectedImage(null);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, images]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      {/* Main Gallery Dialog */}
      <Dialog 
        open={open} 
        onOpenChange={(open) => {
          // Only allow closing if not viewing an image in lightbox
          if (!open && !selectedImage) {
            onClose();
          } else if (!open && selectedImage) {
            // If trying to close while viewing image, just close the lightbox
            setSelectedImage(null);
          } else {
            // Opening - always allow
            if (open) onClose(); // This shouldn't happen but handle it
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden" data-testid="image-gallery-modal">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5" />
                Order Images
                {orderInfo.totalImages > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {orderInfo.totalImages} images
                  </Badge>
                )}
              </DialogTitle>
              <div className="flex items-center gap-2">
                {/* Bulk Delete Button */}
                {selectedImages.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1.5 text-purple-600 border-purple-300 bg-purple-50 hover:bg-purple-100"
                    onClick={() => { fetchAvailableSessions(); setShowMoveDialog(true); }}
                  >
                    <FolderInput className="h-4 w-4" />
                    Move To
                  </Button>
                )}
                {selectedImages.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1.5 text-green-600 border-green-300 bg-green-50 hover:bg-green-100"
                    onClick={handleDuplicate}
                    disabled={duplicating}
                  >
                    {duplicating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    Duplicate ({selectedImages.length})
                  </Button>
                )}
                {selectedImages.length > 0 && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="flex items-center gap-1.5"
                        disabled={bulkDeleting}
                        data-testid="bulk-delete-btn"
                      >
                        {bulkDeleting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                        Delete ({selectedImages.length})
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {selectedImages.length} Images</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will move {selectedImages.length} selected image(s) to trash. You can restore them later if needed.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleBulkDelete}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete All Selected
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {(folderPath || orderInfo?.phoneNumber) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1.5 text-blue-600 border-blue-300 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 transition-colors"
                    onClick={openInImageGallery}
                    data-testid="open-in-gallery-btn"
                  >
                    <FolderOpen className="h-4 w-4" />
                    Open in Gallery
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
            {/* Select All / Deselect All */}
            {images.length > 0 && (
              <div className="flex items-center gap-2 pt-2 border-t mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs flex items-center gap-1.5 hover:bg-slate-100"
                  onClick={toggleSelectAll}
                  data-testid="select-all-btn"
                >
                  {isAllSelected ? (
                    <>
                      <CheckSquare className="h-4 w-4 text-blue-600" />
                      Deselect All
                    </>
                  ) : (
                    <>
                      <Square className="h-4 w-4 text-slate-400" />
                      Select All
                    </>
                  )}
                </Button>
                {selectedImages.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {selectedImages.length} selected
                  </Badge>
                )}
              </div>
            )}
          </DialogHeader>

          {/* Order Info */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground border-b pb-3">
            <span>Phone: <span className="font-mono">{orderInfo.phoneNumber}</span></span>
            <span>Session: <Badge variant="outline">{orderInfo.sessionId}</Badge></span>
          </div>

          {/* Gallery Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : images.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <ImageIcon className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground font-medium">No images yet</p>
              <p className="text-sm text-muted-foreground/70">
                Images will appear here when the customer sends them via WhatsApp
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 p-1">
                {images.map((image, index) => {
                  const isSelected = selectedImages.includes(image.filename);
                  return (
                  <div key={image.path} className={`group relative rounded-lg bg-muted cursor-pointer border-2 transition-all ${isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent hover:border-slate-300'}`} data-testid={`image-thumbnail-${index}`}>
                    {/* Image */}
                    <div className="aspect-square overflow-hidden relative">
                      <img
                        src={`${BACKEND_URL}${image.path}`}
                        alt={image.filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onClick={() => setSelectedImage(image.path)}
                      />
                      {/* Selection checkbox */}
                      <div className={`absolute top-2 left-2 z-10 ${isSelected ? '' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                        <div className={`h-6 w-6 rounded flex items-center justify-center cursor-pointer ${isSelected ? 'bg-blue-500 text-white' : 'bg-black/50 text-white hover:bg-black/70'}`} onClick={(e) => toggleImageSelection(image.filename, e)} data-testid={`select-image-${index}`}>
                          {isSelected && <Check className="h-4 w-4" />}
                        </div>
                      </div>
                      {/* Delete button */}
                      <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="bg-red-500/80 hover:bg-red-500 text-white p-1 rounded" onClick={(e) => e.stopPropagation()}>
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Image</AlertDialogTitle>
                              <AlertDialogDescription>Delete {image.filename}? It will be moved to trash.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteImage(image.filename)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center pointer-events-none">
                        <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      {/* Filename */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <p className="text-xs text-white font-mono truncate">{image.filename}</p>
                      </div>
                    </div>
                    {/* Caption */}
                    <div className="p-1.5 bg-white border-t">
                      {editingCaption === image.path ? (
                        <div className="space-y-1">
                          <input value={captionText} onChange={(e) => setCaptionText(e.target.value.slice(0, 100))} placeholder="Caption (max 100)" className="w-full h-6 text-[10px] px-1.5 border rounded" maxLength={100} autoFocus onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCaption(image.path); if (e.key === 'Escape') { setEditingCaption(null); setCaptionText(""); } }} />
                          <div className="flex justify-between items-center">
                            <span className="text-[8px] text-gray-400">{captionText.length}/100</span>
                            <div className="flex gap-0.5">
                              <button onClick={() => { setEditingCaption(null); setCaptionText(""); }} className="h-5 w-5 flex items-center justify-center bg-gray-200 rounded text-xs">✕</button>
                              <button onClick={() => handleSaveCaption(image.path)} disabled={savingCaption} className="h-5 w-5 flex items-center justify-center bg-green-600 text-white rounded text-xs">✓</button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5 min-h-[18px] flex items-center" onClick={() => { setEditingCaption(image.path); setCaptionText(image.caption || ""); }}>
                          {image.caption && image.caption.trim() ? (
                            <p className="text-[10px] text-gray-700 font-medium line-clamp-1">{image.caption}</p>
                          ) : (
                            <p className="text-[10px] text-gray-300 italic">Add caption...</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      {/* Move To Dialog */}
    {showMoveDialog && (
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Move {selectedImages.length} image(s) to...</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {availableSessions.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No other sessions available</p>
            ) : (
              availableSessions.map(s => (
                <Button key={s.session_id} variant="outline" className="w-full justify-between" onClick={() => handleMoveImages(s.session_id)} disabled={moving}>
                  <span>{s.session_id}</span>
                  <span className="text-xs text-gray-400">{s.image_count} images</span>
                </Button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    )}
    </Dialog>

      {/* Lightbox for Selected Image */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedImage(null);
          }}
          onKeyDown={(e) => e.stopPropagation()}
          data-testid="image-lightbox"
        >
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300 z-50"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setSelectedImage(null);
            }}
          >
            ✕
          </button>

          {/* Previous Button */}
          {images.findIndex((img) => img.path === selectedImage) > 0 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full z-50"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handlePrevImage();
              }}
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
          )}

          {/* Image */}
          <img
            src={`${BACKEND_URL}${selectedImage}`}
            alt="Full size"
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next Button */}
          {images.findIndex((img) => img.path === selectedImage) < images.length - 1 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full z-50"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleNextImage();
              }}
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          )}

          {/* Image Info */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 rounded-lg px-4 py-2 text-white text-sm">
            {images.find((img) => img.path === selectedImage)?.filename} |{" "}
            {images.findIndex((img) => img.path === selectedImage) + 1} of {images.length}
          </div>
        </div>
      )}
    </>
  );
}
